/**
 * Recovery Terminology Utility
 * 
 * Handles the display of recovery time based on user's preferred terminology:
 * - "clean" (commonly used in NA - Narcotics Anonymous)
 * - "sober" (commonly used in AA - Alcoholics Anonymous)
 */

/**
 * Format location for display - handles both string and object formats
 * @param {string|object} location - Location as string or {city, state} object
 * @returns {string} Formatted location string
 */
export const formatLocation = (location) => {
  if (!location) return '';
  if (typeof location === 'string') return location;
  if (typeof location === 'object') {
    const { city, state } = location;
    if (city && state) return `${city}, ${state}`;
    return city || state || '';
  }
  return '';
};

/**
 * Get the recovery time label based on user's terminology preference
 * @param {number} days - Number of days in recovery
 * @param {string} terminology - User's preferred term: 'clean' or 'sober'
 * @returns {string} Formatted string like "2y Clean" or "6m Sober"
 */
export const getRecoveryTimeLabel = (days, terminology = "clean") => {
  if (!days || days <= 0) return "";
  
  const term = terminology === "sober" ? "Sober" : "Clean";
  
  if (days >= 365) {
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    return months > 0 ? `${years}y ${months}m ${term}` : `${years}y ${term}`;
  } else if (days >= 30) {
    const months = Math.floor(days / 30);
    return `${months}m ${term}`;
  }
  return `${days}d ${term}`;
};

/**
 * Get just the time portion without the terminology
 * @param {number} days - Number of days in recovery
 * @returns {string} Formatted string like "2y" or "6 months"
 */
export const getRecoveryTimeDuration = (days) => {
  if (!days || days <= 0) return "";
  
  if (days >= 365) {
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    return months > 0 ? `${years}y ${months}m` : `${years}y`;
  } else if (days >= 30) {
    return `${Math.floor(days / 30)} months`;
  }
  return `${days} days`;
};

/**
 * Get the terminology word only (Clean/Sober)
 * @param {string} terminology - User's preferred term: 'clean' or 'sober'
 * @returns {string} "Clean" or "Sober"
 */
export const getTerminologyWord = (terminology = "clean") => {
  return terminology === "sober" ? "Sober" : "Clean";
};

/**
 * Get a description of recovery time with terminology
 * Used for longer descriptions like "2 years clean" or "6 months sober"
 * @param {number} days - Number of days in recovery
 * @param {string} terminology - User's preferred term: 'clean' or 'sober'
 * @returns {string} Full description
 */
export const getRecoveryDescription = (days, terminology = "clean") => {
  if (!days || days <= 0) return "";
  
  const term = terminology === "sober" ? "sober" : "clean";
  
  if (days >= 365) {
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    if (months > 0) {
      return `${years} ${years === 1 ? 'year' : 'years'} and ${months} ${months === 1 ? 'month' : 'months'} ${term}`;
    }
    return `${years} ${years === 1 ? 'year' : 'years'} ${term}`;
  } else if (days >= 30) {
    const months = Math.floor(days / 30);
    return `${months} ${months === 1 ? 'month' : 'months'} ${term}`;
  }
  return `${days} ${days === 1 ? 'day' : 'days'} ${term}`;
};
